import { getMomentTz } from "@dexteel/mesf-core";
import { InputBase } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import { useState } from "react";
import { toUtcDay } from "../../utils/date";

const INPUT_SX = {
	fontSize: 11,
	"& input": { p: 0, textAlign: "center" },
} as const;

const moment = getMomentTz();

const toNumber = (text: string) =>
	text === "" || text === "-" || text === "." || text === "-."
		? null
		: Number(text);

export const NumberCell = ({
	value,
	align = "right",
	min,
	max,
	integerDigits,
	decimals,
	muted = false,
	onChange,
}: {
	value: number | null;
	align?: "right" | "left";
	min?: number;
	max?: number;
	integerDigits: number;
	decimals: number;
	muted?: boolean;
	onChange: (value: number | null) => void;
}) => {
	const [text, setText] = useState(value === null ? "" : String(value));
	const sign = min !== undefined && min >= 0 ? "" : "-?";
	const pattern = new RegExp(
		`^${sign}\\d{0,${integerDigits}}(\\.\\d{0,${decimals}})?$`,
	);

	const shown =
		toNumber(text) === value ? text : value === null ? "" : String(value);

	return (
		<InputBase
			autoComplete="off"
			value={shown}
			inputProps={{ inputMode: "decimal" }}
			onFocus={(event) => event.target.select()}
			onChange={(event) => {
				const next = event.target.value;
				if (!pattern.test(next)) return;
				const number = toNumber(next);
				if (max !== undefined && number !== null && number > max) return;
				setText(next);
				onChange(number);
			}}
			sx={{
				...INPUT_SX,
				...(muted ? { color: "text.secondary" } : null),
				"& input": { p: 0, textAlign: align },
				width: "100%",
			}}
		/>
	);
};

export const TextCell = ({
	value,
	onChange,
}: {
	value: string | null;
	onChange: (value: string | null) => void;
}) => (
	<InputBase
		autoComplete="off"
		value={value ?? ""}
		onChange={(event) =>
			onChange(event.target.value === "" ? null : event.target.value)
		}
		sx={{ ...INPUT_SX, width: "100%" }}
	/>
);

export const DateCell = ({
	value,
	shouldDisableDate,
	onChange,
}: {
	value: Date | null;
	shouldDisableDate?: (day: Moment) => boolean;
	onChange: (value: Date | null) => void;
}) => (
	<DatePicker
		value={value ? moment.utc(value) : null}
		format="MM/DD/YY"
		shouldDisableDate={shouldDisableDate}
		onChange={(next) => onChange(toUtcDay(next))}
		slotProps={{
			textField: {
				variant: "standard",
				fullWidth: true,
				inputProps: { "data-sections": "" },
				InputProps: {
					disableUnderline: true,
					sx: {
						...INPUT_SX,
						justifyContent: "center",
						"& input": {
							flex: "none",
							p: 0,
							textAlign: "right",
							width: value ? "8ch" : "10ch",
						},
						"& .MuiInputAdornment-root": { ml: 0 },
					},
				},
			},
			openPickerButton: { size: "small", sx: { p: 0.25 } },
			openPickerIcon: { sx: { fontSize: 14 } },
		}}
	/>
);
